import React, { createContext, useContext, useMemo } from 'react';

import { schema } from '../schema';
import { useStaticProps } from './StaticPropsContext';
import { queryParameterContext } from './QueryParameterContext';
import { ApplicationExport, useApplicationExports, ApplicationExportSearchQuery } from '../data/useApplicationExports';

export interface SearchResult {
  applicationExports: Array<ApplicationExport>;
  totalApplicationExports: number;

  isPreviousApplicationExportsData: boolean;
  isLoadingApplicationExports: boolean;
  isFetchingApplicationExports: boolean;
  isApplicationExportsError: boolean;
}

const searchResultContext = createContext<SearchResult | undefined>(undefined);

export const SearchResultContextProvider = ({ children }: { children: React.ReactNode }) => {
  const staticProps = useStaticProps();
  const { parameters } = queryParameterContext.useQueryParameter();

  const searchQueryParams = useMemo(() => {
    const params = {
      'page[number]': 1,
      'page[size]': 20,
    } as ApplicationExportSearchQuery;
    schema.parameters.forEach((parameter) => {
      const value = parameter.getValue({ staticProps, parameters });
      const defaultValue = parameter.getDefaultValue({ defaultParameters: staticProps.defaultParameters });
      const result = parameter.toSearchQuery({
        value,
        staticProps,
        defaultValue,
        parameters,
      });
      if (!result) return;
      Object.assign(params, result);
    });
    return params;
  }, [staticProps, parameters]);

  const {
    applicationExports,
    totalApplicationExports,
    isLoadingApplicationExports,
    isFetchingApplicationExports,
    isPreviousApplicationExportsData,
    applicationExportsError,
  } = useApplicationExports(searchQueryParams);

  const value = useMemo(
    () => ({
      applicationExports,
      totalApplicationExports,
      isPreviousApplicationExportsData,
      isLoadingApplicationExports,
      isFetchingApplicationExports,
      isApplicationExportsError: !!applicationExportsError,
    }),
    [
      applicationExports,
      isPreviousApplicationExportsData,
      totalApplicationExports,
      isLoadingApplicationExports,
      isFetchingApplicationExports,
      applicationExportsError,
    ]
  );

  return <searchResultContext.Provider value={value}>{children}</searchResultContext.Provider>;
};

export const useSearchResult = () => {
  const value = useContext(searchResultContext);
  if (value === undefined) {
    throw new Error('useSearchResult must be used within a SearchResultContextProvider');
  }
  return value;
};
